import React from 'react';
import ProductCardProps from './types/ProductCardProps';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import ProductTag from '../ProductTags/ProductTag';
import { translate } from '../../Services/translation';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import ProductCardLipscoreStars from '../Lipscore/ProductCard.LipscoreStars';
import clsx from 'clsx';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import BuyButton from '../BuyButton/BuyButton';
import ProductCardVariantPicker from './ProductCardVariantPicker';

const ProductCard = (props: ProductCardProps) => {
  const { product, lazyLoadImage, showBuyButton } = props;

  const userInfo = useTypedSelector((state) => state.userInfo);
  const checkout = useTypedSelector((state) => state.checkout);
  const isBusinessCustomer = userInfo?.current?.isBusinessCustomer;

  return (
    <div
      className={clsx(
        'flex flex-col bg-white rounded-none p-3 h-full w-full min-w-0 box-border lg:p-4 relative'
      )}
      style={{ maxHeight: props.maxHeightPx }}
    >
      <div className="flex flex-grow overflow-hidden">
        <a
          href={product.url}
          className="max-h-full flex w-full items-center justify-center "
        >
          <div className="absolute top-2 right-2 z-30 border-1 border-white bg-white rounded-full">
            <FavoriteButton product={product} />
          </div>
          {product.images && product.images.length > 0 && (
            <ResponsiveImage
              {...product.images[0]}
              lazyLoad={lazyLoadImage}
              grow={true}
              pictureClassName="m-0 p-0 max-h-full flex w-full items-center justify-center"
              imgClassName="max-w-full w-full h-auto max-h-full aspect-square object-contain"
            />
          )}
        </a>
        {product.tags && product.tags.length > 0 ? (
          <div className="absolute flex flex-wrap gap-2">
            {product.tags.map((tag) => (
              <ProductTag tagSize="large" id={tag.id} key={tag.id}>
                {tag.name}
              </ProductTag>
            ))}
            {product.discountPercantage && (
              <ProductTag id="discount" tagType="campaign" tagSize="large">
                {product.discountPercantage}
              </ProductTag>
            )}
            {product.isNew && (
              <ProductTag id="isNew" tagSize="large">
                {translate('product-tag.is-new')}
              </ProductTag>
            )}
          </div>
        ) : (
          <>
            {(product.discountPercantage || product.isNew) && (
              <div className="absolute flex flex-wrap gap-2">
                {product.discountPercantage && (
                  <ProductTag
                    id="discount"
                    tagType="campaign"
                    tagSize="large"
                  >
                    {product.discountPercantage}
                  </ProductTag>
                )}
                {product.isNew && (
                  <ProductTag id="isNew" tagSize="large">
                    {translate('product-tag.is-new')}
                  </ProductTag>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div
        className={clsx(
          showBuyButton
            ? 'min-h-[193px] max-h-[193px]'
            : 'min-h-[145px] max-h-[145px]',
          'flex flex-col basis-0 text-sm text-gray-900 pt-4 leading-snug md:text-md'
        )}
      >
        <div className="flex-1 mb-2">
          <a
            className="text-md font-satoshi-bold block mb-2 leading-[1.6rem] max-h-8 overflow-hidden"
            href={product.url}
          >
            {product.name}
          </a>
          {product.productCardDescription && (
            <div className="text-md text-gray-600 leading-[1.6rem] max-h-8 overflow-hidden">
              {product.productCardDescription}
            </div>
          )}
        </div>
        <div className="flex flex-col">
          {product.lipscoreProduct?.productModel && (
            <ProductCardLipscoreStars
              productModel={product.lipscoreProduct.productModel}
              showReviewsCount={false}
            />
          )}
          <div className="flex gap-1">
            {product.fromPrice.hasFromPrice && (
              <span
                className={clsx(
                  'text-md font-satoshi-bold m-0',
                  product.fromPrice.fromPrice.campaignPrice &&
                  'text-primary-500'
                )}
              >
                {translate('product-page.from-product-price')}
              </span>
            )}
            {product.fromPrice.fromPrice.campaignPrice ? (
              <div className="text-md font-satoshi-bold m-0 text-primary-500">
                {product.fromPrice.fromPrice.formattedCampaignPrice}{' '}
                {isBusinessCustomer &&
                  translate('product-card.without-vat')}{' '}
                <span className="line-through text-gray-600">
                  {product.fromPrice.fromPrice.formattedPrice}{' '}
                </span>
              </div>
            ) : (
              product.fromPrice.fromPrice.price && (
                <div className="text-md font-satoshi-bold m-0">
                  {product.fromPrice.fromPrice.formattedPrice}{' '}
                  {isBusinessCustomer &&
                    translate('product-card.without-vat')}
                </div>
              )
            )}
          </div>
          <div className="flex justify-between text-gray-600 text-xs leading-tight md:text-sm">
            <div className="stock">
              {product.hasVariants ? (
                <span>
                  {product.stockStatus.inStock
                    ? translate('product-listing.is-in-stock')
                    : translate('product-listing.is-not-in-stock')}
                </span>
              ) : (
                product.stockStatus.description
              )}
            </div>
            {product.colorRelationAmount != undefined &&
              product.colorRelationAmount > 1 && (
                <div className="color-variants">
                  {product.colorRelationAmount +
                    ' ' +
                    translate('product-card.colors')}
                </div>
              )}
          </div>
          {showBuyButton &&
            (product.variantPickerOptions ? (
              <ProductCardVariantPicker
                product={product}
                className="mt-2"
              />
            ) : (
              <BuyButton
                articleNumber={product.id}
                label={translate('general.buy')}
                className="mt-2"
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
